"use client";

import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  path: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const servicesList: ServiceItem[] = [{
  id: "kitchen-remodeling",
  title: "Kitchen Remodeling",
  description: "Custom cabinetry, durable stone countertops, and tailored layouts designed for cooking, gathering, and everyday living.",
  path: "/kitchen-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_remodel_brookhaven.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_remodel_brookhaven.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_remodel_brookhaven.png-wvc-sizes",
    alt: "Renovated kitchen with clean cabinetry and center island"
  }
}, {
  id: "bathroom-remodeling",
  title: "Bathroom Remodeling",
  description: "Walk-in showers, freestanding soaking tubs, and custom vanities crafted for comfort and long-term durability.",
  path: "/bathroom-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_remodel_brookhaven.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_remodel_brookhaven.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/bathroom_remodel_brookhaven.png-wvc-sizes",
    alt: "Spacious master bathroom with glass shower enclosure and modern vanity"
  }
}, {
  id: "whole-home-remodeling",
  title: "Whole-Home Remodeling",
  description: "Comprehensive interior and structural transformations that bring cohesive design, flow, and modern efficiency to your residence.",
  path: "/whole-home-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_remodel_brookhaven.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_remodel_brookhaven.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_remodel_brookhaven.png-wvc-sizes",
    alt: "Open-concept residential living and dining area after renovation"
  }
}, {
  id: "home-additions",
  title: "Home Additions",
  description: "Seamless second-story extensions, expanded primary suites, and family room bump-outs that match your home's architecture.",
  path: "/home-additions/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_brookhaven.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_brookhaven.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_brookhaven.png-wvc-sizes",
    alt: "Architectural home addition blending with existing exterior structure"
  }
}, {
  id: "basement-remodeling",
  title: "Basement Remodeling",
  description: "Convert unfinished or outdated lower levels into media suites, guest quarters, home gyms, and entertainment rooms.",
  path: "/basement-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodel_brookhaven.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodel_brookhaven.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodel_brookhaven.png-wvc-sizes",
    alt: "Finished basement space featuring recessed lighting and lounge seating"
  }
}, {
  id: "interior-remodeling",
  title: "Interior Remodeling",
  description: "Architectural millwork, wall removals, flooring upgrades, and lighting redesigns that redefine interior spaces.",
  path: "/interior-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/interior_remodel_brookhaven.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/interior_remodel_brookhaven.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/interior_remodel_brookhaven.png-wvc-sizes",
    alt: "Custom architectural millwork and redesigned interior staircase hallway"
  }
}, {
  id: "outdoor-living",
  title: "Outdoor Living",
  description: "Covered patios, outdoor kitchens, and hardscaping installations built for year-round entertaining in Georgia's climate.",
  path: "/outdoor-living/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_brookhaven.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_brookhaven.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_brookhaven.png-wvc-sizes",
    alt: "Covered outdoor patio with stone fireplace and outdoor seating"
  }
}, {
  id: "deck-remodeling",
  title: "Deck Remodeling",
  description: "Custom composite and natural timber decking, integrated rail systems, and multi-tier platforms built to code.",
  path: "/deck-remodeling/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_remodel_brookhaven.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_remodel_brookhaven.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_remodel_brookhaven.png-wvc-sizes",
    alt: "Elevated residential composite deck with clean balustrades"
  }
}];
export default function ServicesGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="servicesgrid" data-nav="light" ref={ref} className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border/60">
      <div className="max-w-7xl mx-auto">
        <div className={`max-w-3xl mb-14 md:mb-18 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground">
            Residential renovation services
          </h2>
          <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed font-default">
            Specialized remodeling and construction tailored to Brookhaven homes, from single-room architectural updates to full-property transformations.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesList.map((service, i) => <div key={service.id} data-index={i} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: isVisible ? `${i * 60}ms` : "0ms"
        }}>
              <Card className="h-full flex flex-col justify-between overflow-hidden border border-border/80 bg-card shadow-xs hover:shadow-md transition-shadow group">
                <div className="aspect-[16/10] w-full overflow-hidden bg-muted relative">
                  <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>

                <CardContent className="flex flex-col flex-1 justify-between p-6">
                  <div>
                    <h3 className="font-serif text-xl font-medium text-foreground tracking-tight group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                    <p className="mt-2.5 text-sm text-foreground/75 leading-relaxed font-default">
                      {service.description}
                    </p>
                  </div>

                  <div className="pt-6 mt-4 border-t border-border/40">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-foreground font-medium hover:bg-muted/50 hover:text-foreground">
                      <Link to={service.path} className="flex items-center justify-between w-full">
                        <span>Explore service</span>
                        <ArrowUpRight className="size-4 text-foreground/70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}